import AppSettingsService from './AppSettingsService';
import {
    AccountInfo,
    Configuration,
    AuthenticationResult,
    PublicClientApplication,
    SilentRequest,
    RedirectRequest,
    EndSessionRequest,
    InteractionRequiredAuthError
} from "@azure/msal-browser";
import React from 'react';

class AuthService {

    msalApplication: PublicClientApplication;
    appSettings: AppSettingsService;
    account?: AccountInfo | null;
    accountInfo?: AuthenticationResult;

    constructor(appSettings: AppSettingsService) {
        if (!appSettings) {
            throw new Error('The app settings service was not provided');
        }

        this.appSettings = appSettings;
        const msalConfig = this.GetMsalClientConfiguration();
        this.msalApplication = new PublicClientApplication(msalConfig);
    }

    // Inicializa la aplicación MSAL antes de cualquier acción
    async initialize(): Promise<void> {
        await this.msalApplication.initialize();
    }

    // Maneja la respuesta de redirect al cargar la página
    async HandlePageLoadEvent(): Promise<AccountInfo | null> {
        await this.initialize();

        const authResult = await this.msalApplication.handleRedirectPromise();
        if (authResult) {
            this.account = authResult.account;
            this.accountInfo = authResult;
            return this.account;
        }

        // No hubo redirect, revisamos cuentas cacheadas
        const currentAccounts = this.msalApplication.getAllAccounts();
        if (!currentAccounts || currentAccounts.length === 0) {
            this.account = null;
            return null;
        }

        if (currentAccounts.length > 1) {
            console.warn("Multiple accounts detected. Selecting the first one.");
        }

        this.account = currentAccounts[0] ?? null;
        return this.account;
    }


    HandleRedirectResponse(authResult: AuthenticationResult | null): void {
        if (authResult !== null) {
            this.account = authResult.account;
            this.accountInfo = authResult;
            // Aquí podrías usar React context si quieres propagar authResult
            const AccountInfoContext = React.createContext(authResult);
        } else {
            const currentAccounts = this.msalApplication.getAllAccounts();

            if (!currentAccounts || currentAccounts.length === 0) {
                // No hay cuentas cacheadas, el usuario debe iniciar sesión
                return;
            } else if (currentAccounts.length > 1) {
                console.warn("Multiple accounts detected in MSAL account cache.");
                this.account = currentAccounts[0] ?? null;
            } else {
                this.account = currentAccounts[0] ?? null;
            }
        }
    }

    GetMsalClientConfiguration(): Configuration {
        return {
            auth: {
                clientId: this.appSettings.GetMsalClientId(),
                authority: this.appSettings.GetMsalTenantAuthorityUri(),
                redirectUri: this.appSettings.GetLoginRedirectUri()
            },
            cache: {
                cacheLocation: this.appSettings.GetMsalCacheLocation(),
                storeAuthStateInCookie: this.appSettings.GetMsalStoreAuthInCookie()
            }
        };
    }

    async GetToken(): Promise<AuthenticationResult> {
        await this.initialize(); // Aseguramos que MSAL esté inicializado

        if (!this.account) {
            throw new Error("No account available to acquire token silently.");
        }

        const tokenRequest: SilentRequest = {
            account: this.account,
            scopes: [this.appSettings.GetMsalClientScope()],
            redirectUri: this.appSettings.GetLoginRedirectUri()
        };

        try {
            return await this.msalApplication.acquireTokenSilent(tokenRequest);
        } catch (error: any) {
            if (error instanceof InteractionRequiredAuthError) {
                // Redirige al login si no se puede renovar silenciosamente
                await this.msalApplication.loginRedirect(tokenRequest);
            } else {
                throw error;
            }
        }

        return this.msalApplication.acquireTokenSilent(tokenRequest);
    }

    async SignIn(): Promise<void> {
        await this.initialize();

        const loginRedirectRequest: RedirectRequest = {
            scopes: [this.appSettings.GetMsalClientScope()],
            prompt: "select_account"
        };

        // Redirige a la página de login
        await this.msalApplication.loginRedirect(loginRedirectRequest);
    }

    async SignOut(): Promise<void> {
        await this.initialize();

        if (!this.account) {
            return;
        }

        const accountInfo = this.msalApplication.getAccountByUsername(this.account.username);
        if (accountInfo) {
            const logoutRequest: EndSessionRequest = { account: accountInfo };
            await this.msalApplication.logout(logoutRequest);
        }
    }
}

export default AuthService;
